import React, { useState, useEffect } from "react";
import { SimpleModulePage } from "../modules/SimpleModulePage";
import { useAuth } from "../context/AuthContext";
import { config } from "../config/environment";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface DietHistoryPageProps {
  onBack: () => void;
}

interface MealData {
  id: string;
  meal_type: string;
  total_calories: number;
  total_protein: number;
  total_carbohydrates: number;
  total_fat: number;
  total_fiber: number;
  food_item_count: number;
  items: Array<{
    id: string;
    food_name: string;
    portion_size: string;
    calories: number;
  }>;
}

export const DietHistoryPage: React.FC<DietHistoryPageProps> = ({ onBack }) => {
  const { isLoggedIn } = useAuth();
  const [dietData, setDietData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [mealData, setMealData] = useState<MealData[]>([]);
  const [loadingMeals, setLoadingMeals] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const observerTarget = React.useRef<HTMLDivElement>(null);

  // ✅ 기간 단위로 식단 데이터 로드 (1년씩 조회)
  useEffect(() => {
    const loadDietHistory = async () => {
      if (!isInitialLoad && !hasMore) return;
      
      const isFirstLoad = offset === 0;
      if (isFirstLoad) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const today = new Date();
        const token = localStorage.getItem('authToken');
        if (!token) return;

        // 기간 단위 조회: offset * 365일씩 추가로 조회
        // offset=0: 최근 365일 (오늘부터 1년 전)
        // offset=1: 1년~2년 전
        const rangeSize = 365;
        const startDaysAgo = offset * rangeSize + rangeSize;
        const endDaysAgo = offset * rangeSize;

        const endDate = new Date(today);
        endDate.setDate(endDate.getDate() - endDaysAgo);
        const endDateStr = endDate.toISOString().split('T')[0];

        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - startDaysAgo);
        const startDateStr = startDate.toISOString().split('T')[0];

        console.log(`[DietHistory] Loading range: ${startDateStr} to ${endDateStr}`);

        // 한 번의 API 호출로 기간 내 모든 데이터 조회
        const response = await fetch(
          `${config.apiUrl}/diet/range?startDate=${startDateStr}&endDate=${endDateStr}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        
        console.log('[DietHistory] API response:', data);
        
        if (data.success && data.data && Array.isArray(data.data)) {
          const filteredData = data.data;
          
          console.log('[DietHistory] Filtered data:', filteredData);
          
          // 페이징: 10개씩 표시
          const pageSize = 10;
          const pagedData = filteredData.slice(0, pageSize);

          console.log('[DietHistory] Paged data:', pagedData);

          if (isFirstLoad) {
            setDietData(pagedData);
          } else {
            setDietData(prev => [...prev, ...pagedData]);
          }

          // 데이터가 부족하면 더 이상 없음으로 표시
          if (filteredData.length < (rangeSize / 10)) {
            setHasMore(false);
          }
        } else {
          console.warn('[DietHistory] Invalid response format:', data);
          setHasMore(false);
        }

        setIsInitialLoad(false);
      } catch (error) {
        console.error('[DietHistory] Error loading data:', error);
        setHasMore(false);
      } finally {
        if (isFirstLoad) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    };

    loadDietHistory();
  }, [offset, isInitialLoad, hasMore]);

  // ✅ IntersectionObserver로 스크롤 감지
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          setOffset(prev => prev + 10);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore]);

  // ✅ selectedDate 변경 시 음식 데이터 로드
  useEffect(() => {
    const loadMealDetails = async () => {
      if (!selectedDate || !isLoggedIn) {
        console.log('[DietHistory] Skipping meal load - selectedDate:', selectedDate, 'isLoggedIn:', isLoggedIn);
        return;
      }
      
      setLoadingMeals(true);
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('[DietHistory] No auth token found');
          return;
        }

        // ✅ selectedDate 검증 및 로깅
        const trimmedDate = selectedDate.trim();
        console.log('[DietHistory] Loading meal details for date:', trimmedDate);
        
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
          console.error('[DietHistory] Invalid date format:', trimmedDate);
          setLoadingMeals(false);
          return;
        }

        const apiUrl = `${config.apiUrl}/diet/logs/${encodeURIComponent(trimmedDate)}`;
        console.log('[DietHistory] Fetching from:', apiUrl);

        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        console.log('[DietHistory] Response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('[DietHistory] Meal data response:', data);
          if (data.success && data.logs) {
            setMealData(data.logs);
          }
        } else {
          const errorData = await response.text();
          console.error('[DietHistory] API error:', response.status, errorData);
        }
      } catch (error) {
        console.error('[DietHistory] Error loading meal details:', error);
      } finally {
        setLoadingMeals(false);
      }
    };

    loadMealDetails();
  }, [selectedDate, isLoggedIn]);

  // ✅ BloodRecordsPage와 동일한 방식으로 날짜 포맷팅
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '날짜 없음';
    
    try {
      // YYYY-MM-DD 형식 → Date 객체 (로컬 타임존 기준)
      const date = new Date(dateStr + 'T00:00:00');
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dayLabels = ["일", "월", "화", "수", "목", "금", "토"];
      const dayLabel = dayLabels[date.getDay()];
      
      return `${year}년 ${month}월 ${day}일 (${dayLabel})`;
    } catch (error) {
      console.error('[formatDate] Error formatting date:', dateStr, error);
      return dateStr || '날짜 오류';
    }
  };

  const mealLabels: Record<string, { label: string; emoji: string }> = {
    breakfast: { label: '아침', emoji: '🌅' },
    lunch: { label: '점심', emoji: '☀️' },
    dinner: { label: '저녁', emoji: '🌙' },
    snack: { label: '야식', emoji: '🍪' }
  };

  if (selectedDate && dietData.find(d => d.date === selectedDate)) {
    const selectedDiet = dietData.find(d => d.date === selectedDate);
    
    if (!selectedDiet) {
      return (
        <SimpleModulePage
          title="식단 기록"
          subtitle="데이터 오류"
          icon="🍽️"
          onBack={() => {
            setSelectedDate(null);
            setMealData([]);
          }}
        >
          <SectionCard>
            <p className="text-center text-[12px] text-slate-500">선택한 날짜의 데이터를 찾을 수 없습니다.</p>
          </SectionCard>
        </SimpleModulePage>
      );
    }
    
    return (
      <SimpleModulePage
        title="식단 기록"
        subtitle={formatDate(selectedDiet.date)}
        icon="🍽️"
        onBack={() => {
          setSelectedDate(null);
          setMealData([]);
        }}
      >
        <SectionCard>
          <div className="mb-3">
            <p className="text-[13px] font-semibold text-slate-900 mb-2">
              {formatDate(selectedDiet.date)}
            </p>
            <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
              <span className="text-[24px]">🍽️</span>
              <div>
                <p className="text-[13px] font-semibold text-slate-900">
                  {isNaN(Number(selectedDiet.totalCalories)) ? 0 : Math.round(Number(selectedDiet.totalCalories))} kcal
                </p>
                <p className="text-[11px] text-slate-600">
                  영양소 기록 완료
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <div className="p-3 border border-slate-200 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] text-slate-600">단백질</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedDiet.nutrition?.protein || 0}g</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-600">탄수화물</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedDiet.nutrition?.carbohydrates || 0}g</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-600">지방</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedDiet.nutrition?.fat || 0}g</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-600">식이섬유</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedDiet.nutrition?.fiber || 0}g</p>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ✅ 식사별 음식 목록 */}
        {loadingMeals ? (
          <SectionCard>
            <p className="text-center text-[12px] text-slate-500">음식 정보 로딩 중...</p>
          </SectionCard>
        ) : mealData.length === 0 ? (
          <SectionCard>
            <p className="text-center text-[12px] text-slate-500">이 날짜의 음식 기록이 없습니다.</p>
          </SectionCard>
        ) : (
          <SectionCard>
            <p className="text-[13px] font-semibold text-slate-900 mb-3">식사별 음식 현황</p>
            <div className="space-y-3">
              {Object.keys(mealLabels).map((mealType) => {
                const meal = mealData.find(m => m.meal_type === mealType);
                const mealLabel = mealLabels[mealType];
                
                return (
                  <div key={mealType} className="border border-slate-200 rounded-lg p-3">
                    {/* 식사 타입 헤더 */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[18px]">{mealLabel.emoji}</span>
                      <p className="text-[13px] font-semibold text-slate-900">{mealLabel.label}</p>
                      {meal && (
                        <span className="text-[11px] text-slate-500">({meal.food_item_count}개)</span>
                      )}
                    </div>

                    {/* 음식 목록 */}
                    {meal && meal.items && meal.items.length > 0 ? (
                      <div className="space-y-1 ml-6">
                        {meal.items.map((item) => (
                          <div key={item.id} className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-b-0">
                            <div className="flex-1">
                              <p className="text-[11px] font-medium text-slate-800">{item.food_name}</p>
                              {item.portion_size && (
                                <p className="text-[9px] text-slate-500">{item.portion_size}</p>
                              )}
                            </div>
                            <p className="text-[11px] font-semibold text-slate-700 ml-2">{Math.round(item.calories || 0)}kcal</p>
                          </div>
                        ))}
                        
                        {/* 식사 소계 */}
                        <div className="mt-2 pt-2 border-t-2 border-slate-300 flex justify-between items-center">
                          <p className="text-[11px] font-semibold text-slate-900">소계</p>
                          <div className="text-right">
                            <p className="text-[12px] font-bold text-slate-900">{Math.round(meal.total_calories || 0)}kcal</p>
                            <p className="text-[9px] text-slate-600">단백질 {Math.round(meal.total_protein || 0)}g</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500 ml-6">기록 없음</p>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}
      </SimpleModulePage>
    );
  }

  return (
    <SimpleModulePage
      title="식단 정보"
      subtitle="식단 기록"
      icon="🍽️"
      onBack={onBack}
    >
      {loading ? (
        <SectionCard>
          <p className="text-center text-[13px] text-slate-600">로딩 중...</p>
        </SectionCard>
      ) : dietData.length === 0 ? (
        <SectionCard>
          <p className="text-center text-[13px] text-slate-600">
            최근 7일간의 식단 기록이 없습니다.
          </p>
        </SectionCard>
      ) : (
        <SectionCard>
          <div className="space-y-0">
            {dietData.map((diet, idx) => (
              <button
                key={diet.date || idx}
                onClick={() => {
                  setSelectedDate(diet.date);
                  setMealData([]);
                }}
                disabled={loadingMeals}
                className={`w-full py-3 px-0 text-left border-b border-slate-100 ${
                  idx === dietData.length - 1 ? 'last:border-b-0' : ''
                } hover:bg-slate-50 transition-colors flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div>
                  <p className="text-[13px] font-semibold text-slate-900 mb-1">
                    {formatDate(diet.date)}
                  </p>
                  <p className="text-[11px] text-slate-600">
                    총 {isNaN(Number(diet.totalCalories)) ? 0 : Math.round(Number(diet.totalCalories))} kcal
                  </p>
                </div>
                <span className="text-slate-400">→</span>
              </button>
            ))}
          </div>

          {/* ✅ 스크롤 감지 대상 */}
          {hasMore && (
            <div
              ref={observerTarget}
              className="w-full py-4 flex justify-center"
            >
              {loadingMore && (
                <p className="text-[12px] text-slate-500">추가 데이터 로드 중...</p>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {/* 안내 */}
      <SectionCard className="bg-blue-50 border-blue-200">
        <div className="flex gap-2">
          <span className="text-[16px] flex-shrink-0">ℹ️</span>
          <div>
            <p className="text-[12px] font-semibold text-blue-900 mb-1">정보 안내</p>
            <p className="text-[11px] text-blue-800 leading-relaxed">
              일자별 식단 기록을 최대 10개씩 표시합니다. 하단으로 스크롤하면 추가 데이터를 자동으로 로드합니다.
            </p>
          </div>
        </div>
      </SectionCard>
    </SimpleModulePage>
  );
};
